import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import { Button } from './button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'

interface PaginationProps {
  page: number
  pageSize: number
  totalCount: number
  pageSizeOptions?: number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

function getVisiblePages(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages: (number | 'ellipsis')[] = []

  // Always show page 1
  pages.push(1)

  // Show ellipsis if current page is far from start
  if (currentPage > 3) {
    pages.push('ellipsis')
  }

  // Show pages around current page
  const start = Math.max(2, currentPage - 1)
  const end = Math.min(totalPages - 1, currentPage + 1)

  for (let i = start; i <= end; i++) {
    if (!pages.includes(i)) {
      pages.push(i)
    }
  }

  // Show ellipsis if current page is far from end
  if (currentPage < totalPages - 2) {
    pages.push('ellipsis')
  }

  // Always show last page
  if (!pages.includes(totalPages)) {
    pages.push(totalPages)
  }

  return pages
}

export function Pagination({
  page,
  pageSize,
  totalCount,
  pageSizeOptions = [10, 20, 50, 100],
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  const totalPages = Math.ceil(totalCount / pageSize)
  const startRecord = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const endRecord = Math.min(page * pageSize, totalCount)

  const canGoPrevious = page > 1
  const canGoNext = page < totalPages

  const visiblePages = getVisiblePages(page, totalPages)

  return (
    <div className="relative flex items-center justify-between px-4 py-3 border-t bg-muted shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.15)]">
      {/* Left: Rows per page */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Rows per page</span>
        <Select
          value={String(pageSize)}
          onValueChange={(value) => onPageSizeChange(Number(value))}
        >
          <SelectTrigger className="h-8 w-[70px] bg-input-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Center: Page navigation */}
      <div className="flex items-center gap-1 ">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!canGoPrevious}
          className="gap-1 text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>

        {visiblePages.map((pageNum, index) =>
          pageNum === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className="px-2">
              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
            </span>
          ) : (
            <Button
              key={pageNum}
              variant={pageNum === page ? 'outline' : 'ghost'}
              size="sm"
              onClick={() => onPageChange(pageNum)}
              className={`min-w-[32px] ${pageNum === page ? 'border-primary bg-input-background' : ''}`}
            >
              {pageNum}
            </Button>
          )
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!canGoNext}
          className="gap-1 text-foreground"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Right: Record count */}
      <span className="text-sm text-muted-foreground">
        {startRecord}-{endRecord} of {totalCount}
      </span>
    </div>
  )
}
